/* eslint-disable react/function-component-definition, no-shadow, no-restricted-globals, react/self-closing-comp, react/no-array-index-key, react/button-has-type, jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';

// GraphQL Queries y Mutations
const ADMIN_PENDING_TOP3_REWARDS = gql`
  query AdminPendingTop3Rewards {
    adminPendingTop3Rewards {
      id
      user {
        id
        name
        profile_photo_path
      }
      sessions
      month
      description
      earned_at
    }
  }
`;

const APPROVE_TOP3_REWARD = gql`
  mutation ApproveTop3SessionsReward($rewardId: ID!, $notes: String) {
    approveTop3SessionsReward(reward_id: $rewardId, notes: $notes) {
      id
      status
    }
  }
`;

const REJECT_TOP3_REWARD = gql`
  mutation RejectTop3SessionsReward($rewardId: ID!, $reason: String!) {
    rejectTop3SessionsReward(reward_id: $rewardId, reason: $reason) {
      id
      status
    }
  }
`;

/**
 * Top3RewardsApproval - Panel de admin para aprobar/rechazar recompensas Top 3
 * Solo accesible para ADMIN
 */
const Top3RewardsApproval = () => {
  const [selectedReward, setSelectedReward] = useState(null);
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  const { loading, error, data, refetch } = useQuery(ADMIN_PENDING_TOP3_REWARDS, {
    fetchPolicy: 'cache-and-network',
  });

  const [approveReward, { loading: approving }] = useMutation(APPROVE_TOP3_REWARD, {
    onCompleted: () => {
      setSelectedReward(null);
      setNotes('');
      refetch();
      alert('Recompensa aprobada exitosamente');
    },
    onError: (error) => {
      alert(`Error: ${error.message}`);
    },
  });

  const [rejectReward, { loading: rejecting }] = useMutation(REJECT_TOP3_REWARD, {
    onCompleted: () => {
      setShowRejectModal(false);
      setSelectedReward(null);
      setRejectReason('');
      refetch();
      alert('Recompensa rechazada');
    },
    onError: (error) => {
      alert(`Error: ${error.message}`);
    },
  });

  const handleApprove = async (reward) => {
    if (!confirm(`¿Aprobar ${reward.sessions} sesiones gratis para ${reward.user.name}?`)) {
      return;
    }

    await approveReward({
      variables: {
        rewardId: reward.id,
        notes: notes || null,
      },
    });
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Debes proporcionar una razón para rechazar');
      return;
    }

    await rejectReward({
      variables: {
        rewardId: selectedReward.id,
        reason: rejectReason,
      },
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">Error cargando recompensas pendientes</p>
      </div>
    );
  }

  const pendingRewards = data?.adminPendingTop3Rewards || [];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Aprobación de Recompensas Top 3
        </h2>
        <p className="text-gray-600">
          Recompensas pendientes de aprobación ({pendingRewards.length})
        </p>
      </div>

      {/* Lista de recompensas pendientes */}
      {pendingRewards.length > 0 ? (
        <div className="space-y-4">
          {pendingRewards.map((reward) => (
            <div
              key={reward.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                {/* Izquierda: Info del usuario */}
                <div className="flex items-center gap-4 flex-1">
                  {/* Avatar */}
                  <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                    {reward.user.profile_photo_path ? (
                      <img
                        src={reward.user.profile_photo_path}
                        alt={reward.user.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-400 to-orange-500 text-white font-bold text-2xl">
                        {reward.user.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Detalles */}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                      {reward.user.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      🏆 {reward.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>🎟️ {reward.sessions} sesiones gratis</span>
                      <span>📅 {reward.month}</span>
                      <span>
                        Ganado: {new Date(reward.earned_at).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Derecha: Acciones */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprove(reward)}
                    disabled={approving}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {approving ? 'Aprobando...' : 'Aprobar'}
                  </button>
                  <button
                    onClick={() => {
                      setSelectedReward(reward);
                      setShowRejectModal(true);
                    }}
                    disabled={rejecting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    Rechazar
                  </button>
                </div>
              </div>

              {/* Campo de notas (opcional) */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notas adicionales (opcional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                  placeholder="Ej: Aprobado por buen desempeño en el mes de enero"
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-6xl mb-4">✅</div>
          <p className="text-gray-600 text-lg">
            No hay recompensas pendientes de aprobación
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Las recompensas aparecerán aquí el primer día de cada mes
          </p>
        </div>
      )}

      {/* Modal de rechazo */}
      {showRejectModal && selectedReward && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Rechazar Recompensa
            </h3>
            <p className="text-gray-600 mb-4">
              ¿Estás seguro de rechazar la recompensa para{' '}
              <strong>{selectedReward.user.name}</strong>?
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Razón del rechazo *
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg p-2 text-sm mb-4"
              placeholder="Explica por qué se rechaza la recompensa"
            />

            <div className="flex gap-2">
              <button
                onClick={handleReject}
                disabled={rejecting || !rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {rejecting ? 'Rechazando...' : 'Confirmar Rechazo'}
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedReward(null);
                  setRejectReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Top3RewardsApproval;
